import { CheckCircleIcon, InfoIcon, WarningCircleIcon, XIcon } from "@phosphor-icons/react";
import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { cn } from "../cn.js";

export type ToastTone = "success" | "error" | "info";

export interface Toast {
	id: string;
	tone: ToastTone;
	message: string;
	/** Offered when the failed action is safe to repeat. */
	onRetry?: () => void;
	durationMs?: number;
}

interface ToastContextValue {
	toast(input: Omit<Toast, "id">): string;
	dismiss(id: string): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
	const context = useContext(ToastContext);
	if (!context) throw new Error("useToast must be used inside <ToastProvider>");
	return context;
}

const TONES: Record<ToastTone, { icon: typeof CheckCircleIcon; classes: string }> = {
	success: {
		icon: CheckCircleIcon,
		classes: "border-(--color-positive-500)/25 bg-(--color-positive-50) text-(--color-positive-700)",
	},
	error: {
		icon: WarningCircleIcon,
		classes: "border-(--color-critical-500)/25 bg-(--color-critical-50) text-(--color-critical-700)",
	},
	info: {
		icon: InfoIcon,
		classes: "border-(--border-subtle) bg-(--surface-raised) text-(--text-primary)",
	},
};

export function ToastProvider({ children }: { children: ReactNode }) {
	const [toasts, setToasts] = useState<Toast[]>([]);
	const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

	const dismiss = useCallback((id: string) => {
		setToasts((current) => current.filter((toast) => toast.id !== id));
		const timer = timers.current.get(id);
		if (timer) {
			clearTimeout(timer);
			timers.current.delete(id);
		}
	}, []);

	const toast = useCallback(
		(input: Omit<Toast, "id">) => {
			const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
			setToasts((current) => [...current, { ...input, id }]);

			// Errors stay until dismissed. A failure the user missed is a
			// failure they will report as "nothing happened".
			const duration = input.durationMs ?? (input.tone === "error" ? 0 : 5000);
			if (duration > 0) {
				timers.current.set(
					id,
					setTimeout(() => dismiss(id), duration),
				);
			}
			return id;
		},
		[dismiss],
	);

	useEffect(() => {
		const pending = timers.current;
		return () => {
			for (const timer of pending.values()) clearTimeout(timer);
			pending.clear();
		};
	}, []);

	const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

	return (
		<ToastContext.Provider value={value}>
			{children}
			<div
				aria-live="polite"
				aria-atomic="false"
				className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end"
			>
				{toasts.map((item) => {
					const { icon: Icon, classes } = TONES[item.tone];
					return (
						<div
							key={item.id}
							role={item.tone === "error" ? "alert" : "status"}
							className={cn(
								"pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-(--radius-lg) border",
								"px-3.5 py-3 text-sm shadow-(--shadow-raised)",
								classes,
							)}
						>
							<Icon className="mt-px size-4 shrink-0" weight="fill" aria-hidden="true" />
							<p className="flex-1">{item.message}</p>
							{item.onRetry && (
								<button
									type="button"
									onClick={() => {
										dismiss(item.id);
										item.onRetry?.();
									}}
									className="shrink-0 font-medium underline underline-offset-2"
								>
									Retry
								</button>
							)}
							<button
								type="button"
								onClick={() => dismiss(item.id)}
								aria-label="Dismiss"
								className="-mr-1 -mt-0.5 shrink-0 rounded-(--radius-xs) p-1 opacity-60 hover:opacity-100"
							>
								<XIcon className="size-3.5" aria-hidden="true" />
							</button>
						</div>
					);
				})}
			</div>
		</ToastContext.Provider>
	);
}
